'use client';
import { createColumnHelper } from '@tanstack/react-table';
import clsx from 'clsx';
import React, { useCallback, useMemo, useState } from 'react';
import { FiMoreVertical, FiPlus, FiUserPlus } from 'react-icons/fi';
import { HiTrash } from 'react-icons/hi';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ActionMenu, ActionMenuItem } from '@/components/actionMenu/actionMenu';
import { Button } from '@/components/button/button';
import { Icon } from '@/components/icon/icon';
import { Loader } from '@/components/loader/loader';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import { Section, Subsection } from '@/components/section/section';
import { SegmentedToggle } from '@/components/SegmentedToggle/segmentedToggle';
import { TabItem, Tabs } from '@/components/tabs/tabs';
import { Heading } from '@/components/typography/heading';
import { Text } from '@/components/typography/text';
import {
  useChangeContractAccess,
  useRevokeContractAccess,
  useSetContractRestrict
} from '@/hooks/mutations/useContractAccessMutations';
import { useContractAccess } from '@/hooks/queries/useDataContracts';
import { useOrgUsersQuery } from '@/hooks/queries/useOrg';
import { ContractRole } from '@/types/dataContracts';
import { User as OrgUser } from '@/types/user';
import { parseParams } from '@/utils/searchParams';

import { AddConstellationModal } from './AddConstellationModal';
import { AddPrincipalModal } from './AddPrincipalModal';
import styles from './ManageAccessSection.module.scss';
import {
  expandPrincipals,
  extractApiErrorMessage,
  partitionRows,
  PrincipalRow
} from './utils';

export type AccessSubTab = 'users' | 'constellations';

const ACCESS_SUB_TAB_VALUES: readonly AccessSubTab[] = [ 'users', 'constellations' ] as const;

function isAccessSubTab( value: string | null | undefined ): value is AccessSubTab {
  return ACCESS_SUB_TAB_VALUES.includes( value as AccessSubTab );
}

const ACCESS_SUB_TABS: TabItem<AccessSubTab>[] = [
  { value: 'users', label: 'Users', 'data-testid': 'manage-access-sub-tab-users' },
  { value: 'constellations', label: 'Constellations', 'data-testid': 'manage-access-sub-tab-constellations' }
];

type Visibility = 'discoverable' | 'private';
const VISIBILITY_OPTIONS: [
  { value: 'discoverable'; label: 'Discoverable' },
  { value: 'private'; label: 'Private' }
] = [
  { value: 'discoverable', label: 'Discoverable' },
  { value: 'private', label: 'Private' }
];

type ManageAccessSectionProps = {
  contractId: string;
  // Shown in the Figma section headings ("Who can manage <Name>" /
  // "Who can query data in <Name>"). Optional so the component is still
  // mountable without it; falls back to the contract id.
  contractName?: string;
};

// principal shape: 'constellation:<uuid>'. UI shows the bare UUID for now;
// a Core-side `GET /constellations?ids=...` lookup is the follow-up that
// adds friendly names + agent counts (tracked as task #72).
// Defensively passes through the full string if the prefix is missing —
// shouldn't happen if the backend emits the design-doc shape, but better
// than rendering empty.
const constellationDisplayName = ( principal: string ): string => {
  if ( principal.startsWith( 'constellation:' ) ) {
    return principal.slice( 'constellation:'.length );
  }
  return principal;
};

// Module-scope stable sentinel — see comment on `orgUsers` below for
// why this matters (memo-invalidation defense).
const EMPTY_USERS: OrgUser[] = [];

export function ManageAccessSection( { contractId, contractName }: ManageAccessSectionProps ) {
  const displayName = contractName ?? contractId;
  const { data: access, isLoading, error } = useContractAccess( contractId );
  // KANP-1894: `useOrgUsersQuery` is configured with
  // `structuralSharing: false`, so `data` reference changes on EVERY
  // refetch (window-focus, mutation invalidation, polling) even when
  // the user list content is identical. Naïvely binding
  // `orgUsersData ?? EMPTY_USERS` would fix only the initial-load case
  // (`undefined → EMPTY_USERS` is stable); once data lands, the
  // reference flips on every refetch and downstream `usersById` +
  // column memos thrash.
  //
  // Pin to a content-stable reference by memoizing on a cheap hash
  // (length + last id). The hash misses the rare swap-of-equal-length
  // case but covers the realistic add/remove flow. Upstream fix
  // (removing `structuralSharing: false` from useOrgUsersQuery) is
  // out of scope here — that hook has multiple consumers.
  const { data: orgUsersData } = useOrgUsersQuery();
  const orgUsersKey = orgUsersData
    ? `${ orgUsersData.length }:${ orgUsersData[ orgUsersData.length - 1 ]?.id ?? '' }`
    : '';
  // Deps INTENTIONALLY exclude orgUsersData — including it would
  // defeat the whole point (memo re-runs on every reference flip).
  // We re-capture the latest data only when the content-hash changes.
  const orgUsers = useMemo(
    () => orgUsersData ?? EMPTY_USERS,
    [ orgUsersKey ]
  );

  const revoke = useRevokeContractAccess( contractId );
  const change = useChangeContractAccess( contractId );
  const restrict = useSetContractRestrict( contractId );

  const [ searchParams, setSearchParams ] = useSearchParams();
  const { sub: qSub = '' } = parseParams<{ sub?: string }>( searchParams );
  const activeSubTab: AccessSubTab = isAccessSubTab( qSub ) ? qSub : 'users';

  const goToSubTab = useCallback( ( next: AccessSubTab ) => {
    setSearchParams( ( prev ) => {
      const params = new URLSearchParams( prev );
      params.set( 'sub', next );
      return params;
    } );
  }, [ setSearchParams ] );

  const [ addMode, setAddMode ] = useState<'owner' | 'data_reader' | null>( null );
  const [ addConstellationOpen, setAddConstellationOpen ] = useState( false );

  const {
    owners, dataReaders, constellations, existingUserIds, existingConstellationPrincipals
  } = useMemo( () => {
    if ( !access ) {
      return {
        owners: [], dataReaders: [], constellations: [],
        existingUserIds: new Set<string>(),
        existingConstellationPrincipals: new Set<string>()
      };
    }
    const rows = expandPrincipals( access.principals, orgUsers );
    const userIds = new Set<string>();
    const constellationPrincipals = new Set<string>();
    rows.forEach( ( r ) => {
      if ( r.type === 'user' ) {
        const userId = r.principal.startsWith( 'user:' ) ? r.principal.slice( 'user:'.length ) : null;
        if ( userId ) userIds.add( userId );
      } else if ( r.type === 'constellation' ) {
        constellationPrincipals.add( r.principal );
      }
    } );
    return {
      ...partitionRows( rows ),
      existingUserIds: userIds,
      existingConstellationPrincipals: constellationPrincipals
    };
  }, [ access, orgUsers ] );

  const handleRevoke = async( row: PrincipalRow ) => {
    if ( row.isAdmin ) {
      // Removing an Admin's contract access would require revoking the
      // org-level admin role — out of scope for this UI. Surface a hint.
      toast.info( 'Admins always have access via their org role. Remove the Admin role to change this.' );
      return;
    }
    try {
      await revoke.mutateAsync( row.principal );
      toast.success( `Removed ${ row.user?.name ?? row.principal }` );
    } catch( err: unknown ) {
      toast.error( extractApiErrorMessage( err, 'Failed to remove access' ) );
    }
  };

  const handleChangeRole = async( row: PrincipalRow, next: ContractRole ) => {
    try {
      await change.mutateAsync( { principal: row.principal, body: { permission: next } } );
      toast.success( `Changed to ${ next === 'owner' ? 'Owner' : 'Data Reader' }` );
    } catch( err: unknown ) {
      toast.error( extractApiErrorMessage( err, 'Failed to change access' ) );
    }
  };

  const handleTogglePii = async( row: PrincipalRow, next: boolean ) => {
    try {
      await change.mutateAsync( { principal: row.principal, body: { pii: next } } );
      toast.success( next ? 'PII access enabled' : 'PII access removed' );
    } catch( err: unknown ) {
      toast.error( extractApiErrorMessage( err, 'Failed to update PII' ) );
    }
  };

  const handleToggleDiscoverable = async( makePrivate: boolean ) => {
    try {
      await restrict.mutateAsync( makePrivate );
      toast.success( makePrivate ? 'Contract is now Private' : 'Contract is now Discoverable' );
    } catch( err: unknown ) {
      toast.error( extractApiErrorMessage( err, 'Failed to toggle visibility' ) );
    }
  };

  // Used to disable row-level buttons while ANY mutation against this
  // contract is in-flight. Prevents a double-click race where the second
  // click sees stale `row.hasPii` / `row.role` before the refetch lands.
  const mutationPending = change.isPending || revoke.isPending || restrict.isPending;

  if ( isLoading ) {
    return <Section title="Data Contracts Access" testId="manage-access"><Loader /></Section>;
  }

  if ( error || !access ) {
    return (
      <Section title="Data Contracts Access" testId="manage-access">
        <Text className={ styles.errorText }>Failed to load access state.</Text>
      </Section>
    );
  }

  const visibility: Visibility = access.is_discoverable ? 'discoverable' : 'private';
  const visibilityToggle = (
    <SegmentedToggle<Visibility>
      value={ visibility }
      options={ VISIBILITY_OPTIONS }
      onChange={ ( next ) => handleToggleDiscoverable( next === 'private' ) }
    />
  );

  return (
    <>
      <Section title="Data Contracts Access" testId="manage-access" headerContent={ visibilityToggle }>
        <Text className={ styles.helpText }>
          { visibility === 'private'
            ? 'Only Owners, Data Readers, and Admins can see this contract.'
            : 'All Developers and Admins in the org can see this contract.' }
        </Text>

        <Tabs
          items={ ACCESS_SUB_TABS }
          active={ activeSubTab }
          onChange={ goToSubTab }
          variant="plain"
          data-testid="manage-access-sub-tabs"
        />

        { activeSubTab === 'users' && (
          <>
            { /* OWNERS — "Who can manage <name>" per Figma 40003779:3999 */ }
            <Subsection testId="manage-access-owners">
              <div className={ styles.subsectionHeader }>
                <div className={ styles.subsectionTitleGroup }>
                  <Heading as="h4" size="md">{ `Who can manage ${ displayName }` }</Heading>
                  <Text className={ styles.subsectionSubtitle }>
                    People who can manage, update, and query onboarded contract data and PII
                  </Text>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={ () => setAddMode( 'owner' ) }
                  icon={ <Icon icon={ FiUserPlus } size="sm" /> }
                  data-testid="manage-access-add-owner-button"
                >
                  Add Owner
                </Button>
              </div>
              <RoleAccessTable
                rows={ owners }
                role="owner"
                showPii={ false }
                pending={ mutationPending }
                onUpdateAccess={ ( r ) => handleChangeRole( r, 'data_reader' ) }
                onRevoke={ handleRevoke }
                emptyText="No Owners yet."
              />
            </Subsection>

            { /* DATA READERS — "Who can query data in <name>" per Figma */ }
            <Subsection testId="manage-access-readers">
              <div className={ styles.subsectionHeader }>
                <div className={ styles.subsectionTitleGroup }>
                  <Heading as="h4" size="md">{ `Who can query data in ${ displayName }` }</Heading>
                  <Text className={ styles.subsectionSubtitle }>
                    People who can query onboarded contract data
                  </Text>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={ () => setAddMode( 'data_reader' ) }
                  icon={ <Icon icon={ FiUserPlus } size="sm" /> }
                  data-testid="manage-access-add-reader-button"
                >
                  Add Data Reader
                </Button>
              </div>
              <RoleAccessTable
                rows={ dataReaders }
                role="data_reader"
                showPii
                pending={ mutationPending }
                onTogglePii={ ( r, next ) => handleTogglePii( r, next ) }
                onUpdateAccess={ ( r ) => handleChangeRole( r, 'owner' ) }
                onRevoke={ handleRevoke }
                emptyText="No Data Readers yet."
              />
            </Subsection>
          </>
        ) }

        { activeSubTab === 'constellations' && (
          <Subsection testId="manage-access-constellations">
            <div className={ styles.subsectionHeader }>
              <div className={ styles.subsectionTitleGroup }>
                <Heading as="h4" size="md">{ `Constellations in ${ displayName }` }</Heading>
                <Text className={ styles.subsectionSubtitle }>
                  Connected constellations can view this contract, its details,
                  and query the onboarded contract data
                </Text>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={ () => setAddConstellationOpen( true ) }
                icon={ <Icon icon={ FiPlus } size="sm" /> }
                data-testid="manage-access-add-constellation-button"
              >
                Add Constellation
              </Button>
            </div>
            <ConstellationsAccessTable
              rows={ constellations }
              orgUsers={ orgUsers }
              pending={ mutationPending }
              onDisconnect={ handleRevoke }
            />
          </Subsection>
        ) }
      </Section>

      <AddPrincipalModal
        contractId={ contractId }
        contractName={ contractName }
        mode={ addMode ?? 'owner' }
        existingUserIds={ existingUserIds }
        // Restored per Melissa's KANP-1848 review (2026-06-19): the Figma
        // 40003779:4625 frame doesn't show this block, but the original
        // design-doc affordance ("On Add Owner, see what constellations
        // are connected — informational only, no propagation") is still
        // load-bearing UX. Owner mode renders it; Data Reader hides it.
        connectedConstellationPrincipals={ Array.from( existingConstellationPrincipals ) }
        isOpen={ addMode !== null }
        onClose={ () => setAddMode( null ) }
      />

      <AddConstellationModal
        contractId={ contractId }
        contractName={ contractName }
        existingConstellationPrincipals={ existingConstellationPrincipals }
        isOpen={ addConstellationOpen }
        onClose={ () => setAddConstellationOpen( false ) }
      />
    </>
  );
}

/**
 * Per-role access table — `Access | Name | Email | [View PII] | Actions`
 * per Figma 40003779:3999. Driven by the shared `NativeTable` component
 * so styling, header markup, and the future expandable sub-row pattern
 * (KANP-1889) stay consistent with the rest of the admin.
 *
 * `onUpdateAccess` is the role-swap handler: "Update access" in the 3-dot
 * menu maps to "Change to Data Reader" on Owner rows and "Change to Owner"
 * on Data Reader rows. Same backing mutation as the old "Change to X"
 * button — Figma just consolidates it under a friendlier label.
 *
 * Empty-state copy lives BELOW the table rather than inside a colSpan'd
 * tbody cell so NativeTable's empty render path doesn't fight us. Header
 * row stays visible either way so the layout doesn't shift.
 */
type RoleAccessTableProps = {
  rows: PrincipalRow[];
  role: ContractRole;
  showPii: boolean;
  pending: boolean;
  onUpdateAccess: ( row: PrincipalRow ) => void;
  onRevoke: ( row: PrincipalRow ) => void;
  onTogglePii?: ( row: PrincipalRow, next: boolean ) => void;
  emptyText: string;
};

const roleColumnHelper = createColumnHelper<PrincipalRow>();

function useRoleAccessColumns(
  role: ContractRole,
  showPii: boolean,
  pending: boolean,
  onUpdateAccess: ( row: PrincipalRow ) => void,
  onRevoke: ( row: PrincipalRow ) => void,
  onTogglePii: ( ( row: PrincipalRow, next: boolean ) => void ) | undefined
) {
  return useMemo( () => {
    const cols = [
      roleColumnHelper.display( {
        id: 'access',
        header: 'Access',
        size: 140,
        cell: ( info ) => {
          const r = info.row.original;
          const label = r.isAdmin ? 'Owner (Admin)' : ( role === 'owner' ? 'Owner' : 'Data Reader' );
          // Row-level testid moved here (NativeTable doesn't expose a
          // row-testid prop) so tests can still locate a row by principal.
          return (
            <span
              className={ styles.accessBadge }
              data-testid={ `manage-access-row-${ r.principal }` }
            >
              { label }
            </span>
          );
        }
      } ),
      roleColumnHelper.accessor( ( r ) => r.user?.name ?? r.principal, {
        id: 'name',
        header: 'Name',
        size: 200
      } ),
      roleColumnHelper.accessor( ( r ) => r.user?.email ?? '—', {
        id: 'email',
        header: 'Email',
        size: 280
      } ),
      ...( showPii ? [
        roleColumnHelper.display( {
          id: 'pii',
          header: 'View PII',
          size: 140,
          cell: ( info ) => {
            const r = info.row.original;
            if ( r.isAdmin ) {
              // Admins always have PII via the org branch — '—' conveys
              // "not editable" while keeping the column aligned.
              return <span className={ styles.piiNotEditable }>—</span>;
            }
            const name = r.user?.name ?? r.principal;
            return (
              <label className={ styles.piiSwitch } aria-label={ `PII access for ${ name }` }>
                <input
                  type="checkbox"
                  checked={ r.hasPii }
                  onChange={ ( e ) => onTogglePii?.( r, e.target.checked ) }
                  disabled={ pending || !onTogglePii }
                />
                <span>{ r.hasPii ? 'Enabled' : 'Disabled' }</span>
              </label>
            );
          }
        } )
      ] : [] ),
      roleColumnHelper.display( {
        id: 'actions',
        header: '',
        size: 60,
        cell: ( info ) => {
          const r = info.row.original;
          if ( r.isAdmin ) {
            return <span className={ styles.actionsPlaceholder } aria-hidden="true">·</span>;
          }
          return (
            <ActionMenu
              icon={ <Icon icon={ FiMoreVertical } size="md" /> }
              disabled={ pending }
              data-testid={ `manage-access-row-actions-${ r.principal }` }
            >
              <ActionMenuItem
                value="Update access"
                icon={ <FiUserPlus size={ 16 } /> }
                onClick={ () => onUpdateAccess( r ) }
                data-testid={ `manage-access-update-${ r.principal }` }
              />
              <ActionMenuItem
                value="Remove user"
                icon={ <HiTrash size={ 16 } /> }
                onClick={ () => onRevoke( r ) }
                data-testid={ `manage-access-remove-${ r.principal }` }
              />
            </ActionMenu>
          );
        }
      } )
    ];
    return cols;
  }, [ role, showPii, pending, onUpdateAccess, onRevoke, onTogglePii ] );
}

function RoleAccessTable( {
  rows, role, showPii, pending, onUpdateAccess, onRevoke, onTogglePii, emptyText
}: RoleAccessTableProps ) {
  const columns = useRoleAccessColumns( role, showPii, pending, onUpdateAccess, onRevoke, onTogglePii );
  const table = useNativeTable( { columns, data: rows, getRowId: ( r ) => r.principal } );

  return (
    <div data-testid={ `manage-access-table-${ role }` }>
      <NativeTable table={ table } pagination={ false } />
      { rows.length === 0 && (
        <Text
          className={ styles.empty }
          data-testid={ `manage-access-empty-${ role }` }
        >
          { emptyText }
        </Text>
      ) }
    </div>
  );
}

/**
 * Constellations table — `Constellation Name | Your Access | Connected By |
 * Actions`. v1 flat list — expandable agent-count rows from Figma 4300
 * are deferred to KANP-1889 (blocked on MAS exposing per-constellation
 * agent data). NativeTable already supports the expandable sub-row
 * pattern via `renderSubRow`, so KANP-1889 slots in as additions, not
 * a rewrite.
 *
 * Your Access + Connected By are populated by the DCM backend per
 * KANP-1894. Both default to `—` when missing — covers the rollout
 * window where the backend hasn't shipped yet and the legacy case
 * where a grant was written before KANP-1834 (no actor in audit_log).
 *
 * NOTE — Figma 40003779:4300 shows a "View PII" column with
 * Enabled/Disabled toggles. Intentionally NOT rendered here: DCM's
 * `tuple_writer.grant_constellation` is hard-coded to write only
 * `user_querier`/`service_querier`/`api_key_querier` tuples; PII is
 * explicitly excluded ("locked decision" — `tuple_writer.py:541`).
 * The FGA model agrees: `user_pii_querier: [user]` (model.fga:269) —
 * PII is only grantable to direct `user:` subjects, never via a
 * constellation cascade. Rendering the column would always show
 * "Disabled" with no path to enable it; surfacing a column that
 * suggests it's togglable is misleading. If MAS introduces per-agent
 * PII grants in the future (Ryan's Step 2), revisit then.
 */
type ConstellationsAccessTableProps = {
  rows: PrincipalRow[];
  // Used to display Connected By as "Name" rather than the raw
  // `user:auth0|X` principal. Same cache the picker already loaded;
  // no extra round trip.
  orgUsers: OrgUser[];
  pending: boolean;
  onDisconnect: ( row: PrincipalRow ) => void;
};

const constellationColumnHelper = createColumnHelper<PrincipalRow>();

// "Owner" / "Invoker" / "No Access" — the constellation-grant tier
// labels from Figma 40003779:4300. Maps from the backend's
// `your_access` enum (see [[reference-dcm-constellation-pii-locked]]
// for the FGA-relation derivation). The enum value 'member' is the
// constellation-invoker tier — NOT the admin_middleware org-tier
// `member` role (KANP-1481) — name collision, different concept.
// We keep 'member' as the wire value (backend API stability) and
// translate to "Invoker" only at the UI label boundary.
const YOUR_ACCESS_LABELS: Record<NonNullable<PrincipalRow['yourAccess']>, string> = {
  owner: 'Owner',
  // Figma 40003779:4300 surfaces the FGA relation name verbatim
  // ("Invoker") rather than translating to a friendlier label like
  // "Member". Backend keeps the enum value as 'member' since that
  // ships in the API; the UI label is the only place that says
  // "Invoker".
  member: 'Invoker',
  none: 'No Access'
};

function useConstellationsColumns(
  pending: boolean,
  orgUsers: OrgUser[],
  onDisconnect: ( row: PrincipalRow ) => void
) {
  // Index by id once per render for the connected-by lookup; O(1)
  // hits instead of O(n) array.find on every cell.
  const usersById = useMemo(
    () => new Map( orgUsers.map( ( u ) => [ u.id, u ] ) ),
    [ orgUsers ]
  );

  return useMemo( () => [
    constellationColumnHelper.accessor( ( r ) => constellationDisplayName( r.principal ), {
      id: 'name',
      header: 'Constellation Name',
      size: 260,
      cell: ( info ) => {
        const r = info.row.original;
        return (
          <span data-testid={ `manage-access-constellation-${ r.principal }` }>
            { constellationDisplayName( r.principal ) }
          </span>
        );
      }
    } ),
    constellationColumnHelper.display( {
      id: 'yourAccess',
      header: 'Your Access',
      size: 160,
      cell: ( info ) => {
        const r = info.row.original;
        // Backend not on KANP-1894 yet OR no caller subject was
        // forwarded → render `—`. Same affordance as the legacy
        // placeholder so users can't tell pre- from post-rollout.
        //
        // Safe lookup: an unknown enum value (backend schema drift,
        // e.g. 'OWNER' case mismatch or a new tier we haven't taught
        // the UI about) would otherwise render an empty styled pill
        // with no text — silent visual bug. `label` undefined → `—`.
        const label = r.yourAccess
          ? YOUR_ACCESS_LABELS[ r.yourAccess ]
          : undefined;
        if ( !label ) {
          return <span className={ styles.piiNotEditable }>—</span>;
        }
        // "No Access" is the semantic opposite of "Owner" / "Invoker"
        // — show it muted so a viewer can scan the column at a glance
        // and tell denials from grants without reading every label.
        const muted = r.yourAccess === 'none';
        return (
          <span className={ clsx( styles.accessBadge, muted && styles.accessBadgeMuted ) }>
            { label }
          </span>
        );
      }
    } ),
    constellationColumnHelper.display( {
      id: 'connectedBy',
      header: 'Connected By',
      size: 280,
      cell: ( info ) => {
        const r = info.row.original;
        const principal = r.connectedBy;
        if ( !principal ) {
          return <span className={ styles.piiNotEditable }>—</span>;
        }
        // Backend should only ever emit `user:` principals here
        // (grants written by users). Anything else (service:, api_key:,
        // empty) is a backend bug or future-typing — render `—` rather
        // than dumping a service/key principal in a column labeled
        // "Connected By" where a viewer would misread it as a person.
        if ( !principal.startsWith( 'user:' ) ) {
          return <span className={ styles.piiNotEditable }>—</span>;
        }
        // Join against the org users cache for display. Falls back to
        // the raw principal when the user isn't in the cache (e.g.
        // they were removed from the org since granting access) —
        // better to show something than nothing.
        const userId = principal.slice( 'user:'.length );
        const user = usersById.get( userId );
        // `||` (not `??`) so empty-string `name: ''` falls through to
        // email — surprisingly common when an org user's profile has
        // a blank name field but a valid email.
        const label = user?.name || user?.email || principal;
        return <span>{ label }</span>;
      }
    } ),
    constellationColumnHelper.display( {
      id: 'actions',
      header: '',
      size: 60,
      cell: ( info ) => {
        const r = info.row.original;
        return (
          <ActionMenu
            icon={ <Icon icon={ FiMoreVertical } size="md" /> }
            disabled={ pending }
            data-testid={ `manage-access-constellation-actions-${ r.principal }` }
          >
            <ActionMenuItem
              value="Disconnect constellation"
              icon={ <HiTrash size={ 16 } /> }
              onClick={ () => onDisconnect( r ) }
              data-testid={ `manage-access-constellation-disconnect-${ r.principal }` }
            />
          </ActionMenu>
        );
      }
    } )
  ], [ pending, onDisconnect, usersById ] );
}

function ConstellationsAccessTable( {
  rows, orgUsers, pending, onDisconnect
}: ConstellationsAccessTableProps ) {
  const columns = useConstellationsColumns( pending, orgUsers, onDisconnect );
  const table = useNativeTable( { columns, data: rows, getRowId: ( r ) => r.principal } );

  return (
    <div data-testid="manage-access-table-constellations">
      <NativeTable table={ table } pagination={ false } />
      { rows.length === 0 && (
        <Text
          className={ styles.empty }
          data-testid="manage-access-empty-constellations"
        >
          No constellations connected.
        </Text>
      ) }
    </div>
  );
}
